import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../utils/api';
import './CoursePage.css';

export default function CoursePage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/courses/${slug || 'your-first-rental-property'}`).then(r => setCourse(r.data));
    if (user) api.get(`/courses/${slug || 'your-first-rental-property'}/progress`).then(r => setProgress(r.data)).catch(() => null);
    setLoading(false);
  }, [slug, user]);

  const enrol = async () => {
    if (!user) { nav('/register', { state: { from: `/courses/${slug}` } }); return; }
    setPaying(true);
    try {
      const { data } = await api.post('/payments/checkout', { course_slug: slug || 'your-first-rental-property' });
      window.location.href = data.url;
    } catch (err) {
      if (err.response?.status === 409) { toast('Already enrolled!', 'success'); nav('/dashboard'); }
      else toast('Payment setup failed — please try again.', 'error');
      setPaying(false);
    }
  };

  if (loading || !course) return <div className="loading-center"><div className="spinner" /></div>;

  const enrolled = progress?.enrolled;

  return (
    <div className="course-page">
      {/* HERO */}
      <section className="cp-hero">
        <div className="cp-hero-inner">
          <span className="badge badge-teal" style={{ marginBottom: '12px' }}>Course 1</span>
          <h1>{course.title}</h1>
          <p className="cp-hero-tagline">{course.tagline}</p>
          <div className="cp-hero-meta">
            <span>📚 {course.module_count} modules</span>
            <span>🎬 {course.lesson_count} lessons</span>
            <span>🤖 Amanda AI included</span>
            <span>🔓 90 day access</span>
          </div>
        </div>
      </section>

      <div className="cp-body">
        <div className="cp-content">
          {/* WHAT YOU'LL LEARN */}
          <div className="card card-p" style={{ marginBottom: '24px' }}>
            <h2 style={{ marginBottom: '16px' }}>What you'll learn</h2>
            <p>{course.description}</p>
          </div>

          {/* MODULES */}
          <h2 style={{ marginBottom: '16px' }}>Course Modules</h2>
          {course.modules?.map((m, i) => (
            <div key={m.id} className="cp-module card">
              <div className="cp-mod-head" onClick={e => e.currentTarget.parentElement.classList.toggle('open')}>
                <div className="cp-mod-num">{i + 1}</div>
                <div className="cp-mod-info">
                  <div className="cp-mod-title">{m.title}</div>
                  <div className="cp-mod-meta">{m.lessons?.filter(l => l).length || 0} lessons</div>
                </div>
                <span className="cp-mod-arrow">›</span>
              </div>
              <div className="cp-mod-lessons">
                {m.lessons?.filter(l => l).map((l, li) => (
                  <div key={l.id} className="cp-lesson-row">
                    <span className="cp-lesson-num">{li + 1}</span>
                    <span className="cp-lesson-title">{l.title}</span>
                    <span className="cp-lesson-dur">{l.duration_mins}m</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* SIDEBAR CTA */}
        <aside className="cp-sidebar">
          <div className="cp-buy-card card card-p">
            <div className="cp-price">£{((course.price_pence || 4700) / 100).toFixed(0)}</div>
            <div className="cp-price-sub">One-time payment · 90 day access </div>
            {enrolled ? (
              <button className="btn btn-primary btn-full mt-2" onClick={() => nav('/dashboard')}>Continue Learning →</button>
            ) : (
              <button className={`btn btn-primary btn-full mt-2 ${paying ? 'btn-loading' : ''}`} onClick={enrol} disabled={paying}>
                {paying ? '' : 'Enrol Now →'}
              </button>
            )}
            {!user && <p style={{ fontSize: '13px', textAlign: 'center', marginTop: '10px', color: 'var(--color-text-body)' }}>Account created at checkout</p>}
            <div className="divider" />
            <div className="cp-includes">
              <div className="cp-include-item">✓ {course.lesson_count} video lessons</div>
              <div className="cp-include-item">✓ Amanda AI coach in every lesson</div>
              <div className="cp-include-item">✓ Downloadable worksheets</div>
              <div className="cp-include-item">✓ Completion certificate</div>
              <div className="cp-include-item">✓ 90 day access </div>
              <div className="cp-include-item">✓ UK-specific content</div>
            </div>
          </div>
          <p style={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center', marginTop: '12px', lineHeight: '1.5' }}>
            Not financial advice. All content is for educational purposes only.
          </p>
        </aside>
      </div>
    </div>
  );
}
